// ABOUTME: Reset password page component where users can set a new password using the token from email
// ABOUTME: Validates password strength and handles token verification with appropriate error messages
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Lock as LockIcon, 
  ArrowBack as ArrowBackIcon, 
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { resetPassword } from '../services/api';
import { validatePassword } from '../utils/passwordValidation';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Get token from URL parameters
  const token = searchParams.get('token');

  // Redirect if no token is provided
  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!isFormValid()) {
      setError('Please correct the errors below.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await resetPassword(token, formData.password);
      setIsSuccess(true);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError(err.response?.data?.message || 'Invalid or expired reset token');
      } else if (axios.isAxiosError(err) && err.response?.status === 500) {
        setError('An error occurred. Please try again.');
      } else {
        setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Password reset failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleBlur = (field: 'password' | 'confirmPassword') => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordValidationError = (): string | null => {
    if (!touched.password || formData.password.length === 0) return null;
    return validatePassword(formData.password);
  };

  const getConfirmPasswordError = (): string | null => {
    if (!touched.confirmPassword || formData.confirmPassword.length === 0) return null;
    if (formData.password !== formData.confirmPassword) {
      return "Passwords don't match";
    }
    return null;
  };

  const isFormValid = (): boolean => {
    const passwordValid = formData.password.length > 0 && !validatePassword(formData.password);
    const confirmPasswordValid = formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;
    return passwordValid && confirmPasswordValid;
  };

  if (isSuccess) {
    return (
      <Container 
        maxWidth="sm" 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          py: 3,
          background: theme => theme.palette.gradient.secondary,
        }}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center">
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'success.main',
                color: 'success.contrastText',
                mx: 'auto',
                mb: 2
              }}
            >
              <CheckCircleIcon fontSize="large" />
            </Avatar>
            
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Password Reset Successful
            </Typography>
            
            <Card sx={{ mt: 3, mb: 4 }}>
              <CardContent>
                <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                  Your password has been successfully reset.
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  You can now log in with your new password.
                </Typography>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Continue to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!token) {
    return (
      <Container 
        maxWidth="sm" 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          py: 3,
          background: theme => theme.palette.gradient.secondary,
        }}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center">
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'error.main',
                color: 'error.contrastText',
                mx: 'auto',
                mb: 2
              }}
            >
              <LockIcon fontSize="large" />
            </Avatar>
            
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Invalid Reset Link
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              This password reset link is invalid or has expired.
              Please request a new password reset.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/forgot-password')}
                fullWidth
              >
                Request New Reset Link
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/login')}
                fullWidth
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        py: 3,
        background: theme => theme.palette.gradient.secondary,
      }}
    >
      <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Avatar 
            sx={{ 
              width: 64, 
              height: 64, 
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              mx: 'auto',
              mb: 2
            }}
          >
            <LockIcon fontSize="large" />
          </Avatar>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Reset Your Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your new password below to complete the reset process.
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="New Password"
            name="password"
            type={showPasswords.password ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            onBlur={handleBlur('password')}
            required
            variant="outlined"
            margin="normal"
            error={!!getPasswordValidationError()}
            helperText={getPasswordValidationError() || "Must be between 8 and 64 characters"}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('password')}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            name="confirmPassword"
            type={showPasswords.confirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            onBlur={handleBlur('confirmPassword')}
            required
            variant="outlined"
            margin="normal"
            error={!!getConfirmPasswordError()}
            helperText={getConfirmPasswordError() || "Re-enter the password to confirm"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isFormValid() || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <LockIcon />}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Box>

        {/* Back to Login */}
        <Box textAlign="center" mt={3}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              textDecoration: 'none', 
              '&:hover': { textDecoration: 'underline' } 
            }}
          >
            <ArrowBackIcon fontSize="small" />
            Back to Login
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;